/**
 * オファー関連 UI 共通の表示用フォーマッタ。
 *
 * `OfferSummaryCard`（送信／受領／オンボーディング契約確認）と
 * `SystemMessageContent`（DM 内のオファー詳細システムメッセージ）の双方で
 * **同一の表記**にするため、ここに集約する。
 *
 * 既存の `DateDisplay` コンポーネントは React 要素を返す UI 部品なので、
 * 値を文字列として組み立てたい用途には使えない。本モジュールは純粋な
 * 文字列フォーマット関数のみを提供する。
 */

import { formatYen, toYen } from "./money";
import { addConsumptionTax } from "./tax";

const WEEKDAY_JP = ["日", "月", "火", "水", "木", "金", "土"] as const;

/**
 * ISO 8601 文字列を `YYYY年M月D日（曜）HH:MM` 形式に整形する（オファーの納期表示）。
 *
 * - `null` / `undefined` / 不正な値の場合は `fallback`（既定 `null`）を返す。
 * - 24 時間表記。月・日は前ゼロなし、時・分は 2 桁ゼロ埋め。
 *   例: `2026年7月12日（日）18:00`
 */
export const formatOfferDateTime = (
  iso: string | null | undefined,
  fallback: string | null = null,
): string | null => {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  const yyyy = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const wd = WEEKDAY_JP[d.getDay()];
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}年${m}月${day}日（${wd}）${hh}:${mi}`;
};

/**
 * 税抜の対価（`contract_amount`）を税込の `○円（税込）` 表記に整形する。
 *
 * - 円は常に整数（端数は四捨五入、`toYen`）。例: `11,000円（税込）`
 * - `null` / `undefined` / 非有限値は `fallback`（既定 `null`）を返す。
 */
export const formatOfferAmountWithTax = (
  amountExclTax: number | null | undefined,
  fallback: string | null = null,
): string | null => {
  if (amountExclTax === null || amountExclTax === undefined) return fallback;
  if (!Number.isFinite(amountExclTax)) return fallback;
  return `${toYen(addConsumptionTax(amountExclTax)).toLocaleString("ja-JP")}円（税込）`;
};

/**
 * 受諾時に受託者へ付与するポジションを「招聘文言」に整形する（オファー要点表示用）。
 *
 * - `PRODUCER`（責任者）: `プロデューサー（責任者）として招聘`
 * - `DIRECTOR`         : `プロデューサーとして招聘`
 * - `SPECIALIST`       : `null`（スペシャリストは表記不要のため非表示）
 * - `null` / `undefined`（変更なし）: `null`（非表示）
 */
export const formatOfferAcceptPositionInvite = (
  position: string | null | undefined,
): string | null => {
  switch (position) {
    case "PRODUCER":
      return "プロデューサー（責任者）として招聘";
    case "DIRECTOR":
      return "プロデューサーとして招聘";
    default:
      return null;
  }
};

/**
 * ロールと担当範囲（スコープ）を `ロール（範囲）` の 1 行表記にまとめる。
 *
 * - 両方あり: `作曲（楽曲A）`
 * - 片方のみ: その値のみ
 * - どちらも無い: `fallback`（既定 `null`）
 *
 * オファー要点表示（OfferSummaryCard / DM のオファー詳細）で共通利用する。
 */
export const formatOfferRoleScope = (
  role: string | null | undefined,
  scope: string | null | undefined,
  fallback: string | null = null,
): string | null => {
  const r = (role ?? "").trim();
  const s = (scope ?? "").trim();
  if (r && s) return `${r}（${s}）`;
  return r || s || fallback;
};

/**
 * 数値を日本円表記（`¥1,234`）に整形する。
 *
 * 円は常に整数で表示する方針のため、実体は `money.formatYen` に委譲する
 * （小数は四捨五入で整数化される）。新規実装では `money.formatYen` を
 * 直接使うこと。既存呼び出し互換のため残置している。
 */
export const formatJpyAmount = formatYen;
